import {
	type Address,
	type BondRequestBondType,
	bondRequestBondTypeDef,
	type ContractBondSubtype,
	contractBondSubtypeDef,
	Dtos,
	discriminatedUnionWithFallback,
	isCommercialSuretyBondType,
	isContractCategoryBondType,
} from "@inrev/common";
import { Option } from "@inrev/core";
import type { Path } from "react-hook-form";
import { z } from "zod";
import { getSchemaDefaults } from "../../../../utils/form";
import {
	commercialBondFormSchema,
	commercialBondSubmitSchema,
} from "../../../shared/bond/commercialBondSchema";
import { withPrincipalMatchesCompany } from "../../../shared/bond/principal";

// ─── Account Schemas (from DTO) ─────────────────────────────────────────────

const AccountRequest = Dtos.SuretyAccount.Create.Request;
const QuoteSchemas = Dtos.SuretyQuote.Create;
const { minimalBidQuoteFieldOverrides, minimalFinalQuoteFieldOverrides } = QuoteSchemas;

const accountDiscriminatedUnion = discriminatedUnionWithFallback(
	"modelSize",
	[
		AccountRequest.aiSmallAccountSubmitSchema,
		AccountRequest.aiMediumAccountSubmitSchema,
		AccountRequest.aiLargeAccountSubmitSchema,
	],
	AccountRequest.aiSmallAccountSubmitSchema,
	{ discriminatorErrorMessage: "Required" },
);

export const accountFormValidationSchema = z.object({
	account: accountDiscriminatedUnion,
	requiresPaymentAndPerformanceBonds: z.boolean().optional(),
});

export const getAccountFormValidationSchema = ({
	requirePaymentAndPerformanceBonds,
}: {
	requirePaymentAndPerformanceBonds: boolean;
}) =>
	requirePaymentAndPerformanceBonds
		? accountFormValidationSchema.extend({
				requiresPaymentAndPerformanceBonds: z.boolean({ error: "Required" }),
			})
		: accountFormValidationSchema;

const AccountCreationQuoteRequest = QuoteSchemas.Request;

const minimalBidQuoteSchemaForAccountCreation = z.object({
	...AccountCreationQuoteRequest.bidQuoteSchemaForAccountCreation.shape,
	...minimalBidQuoteFieldOverrides,
});

const minimalFinalQuoteSchemaForAccountCreation = z.object({
	...AccountCreationQuoteRequest.finalQuoteSchemaForAccountCreation.shape,
	...minimalFinalQuoteFieldOverrides,
});

const minimalQuoteSchemaForAccountCreation = z.discriminatedUnion("contractSuretyType", [
	minimalBidQuoteSchemaForAccountCreation,
	minimalFinalQuoteSchemaForAccountCreation,
	AccountCreationQuoteRequest.bidToFinalQuoteSchemaForAccountCreation,
]);

// Legacy (non-minimal) mode collects only project.description, never project.name.
const requiredDescriptionProjectSchema = z.object({
	...QuoteSchemas.projectSchema.shape,
	// nullish input keeps form defaults compatible; pipe output is hard-required for asterisk derivation.
	description: z.string().nullish().pipe(z.string().trim().min(1, "Required")),
});

const fullQuoteSchemaForAccountCreation = z.discriminatedUnion("contractSuretyType", [
	z.object({
		...AccountCreationQuoteRequest.bidQuoteSchemaForAccountCreation.shape,
		project: requiredDescriptionProjectSchema,
	}),
	z.object({
		...AccountCreationQuoteRequest.finalQuoteSchemaForAccountCreation.shape,
		project: requiredDescriptionProjectSchema,
	}),
	AccountCreationQuoteRequest.bidToFinalQuoteSchemaForAccountCreation,
]);

export const getCombinedFormValidationSchema = ({
	minimalMode,
	requirePaymentAndPerformanceBonds = false,
	commercialSuretyEnabled = false,
	bondType,
}: {
	minimalMode: boolean;
	requirePaymentAndPerformanceBonds?: boolean;
	commercialSuretyEnabled?: boolean;
	bondType?: BondRequestBondType;
}) => {
	const quoteSchema = minimalMode
		? minimalQuoteSchemaForAccountCreation
		: fullQuoteSchemaForAccountCreation;
	const isCommercialBond = commercialSuretyEnabled && isCommercialSuretyBondType(bondType);
	const base = getAccountFormValidationSchema({ requirePaymentAndPerformanceBonds }).extend({
		bondType: z.enum(bondRequestBondTypeDef).optional(),
		contractBondType: z.enum(contractBondSubtypeDef).optional(),
		commercialBond: commercialBondFormSchema.optional(),
	});

	if (!commercialSuretyEnabled) {
		return withPrincipalMatchesCompany(base.extend({ quote: quoteSchema }));
	}

	if (isCommercialBond) {
		return base.extend({
			bondType: z.enum(bondRequestBondTypeDef, { error: "Required" }),
			quote: z.any().optional(),
			commercialBond: commercialBondSubmitSchema,
		});
	}

	return withPrincipalMatchesCompany(
		base
			.extend({
				bondType: z.enum(bondRequestBondTypeDef, { error: "Required" }),
				quote: quoteSchema,
			})
			.superRefine((val, ctx) => {
				if (isContractCategoryBondType(val.bondType) && !val.contractBondType) {
					ctx.addIssue({
						code: "custom",
						path: ["contractBondType"],
						message: "Required",
					});
				}
			}),
	);
};

export const combinedFormValidationSchema = getCombinedFormValidationSchema({ minimalMode: false });

// ─── Form Data Type ─────────────────────────────────────────────────────────

export type AccountCreateFormData = z.infer<typeof accountFormValidationSchema>;

export type CombinedFormData = z.infer<ReturnType<typeof getCombinedFormValidationSchema>>;

export type AccountFormValues = {
	account: z.infer<typeof AccountRequest.aiAccountSchema>;
	quote?: z.infer<typeof QuoteSchemas.Request.quoteSchemaForAccountCreation>;
	requiresPaymentAndPerformanceBonds?: boolean;
	bondType?: BondRequestBondType;
	contractBondType?: ContractBondSubtype;
	commercialBond?: z.infer<typeof commercialBondFormSchema>;
};

// ─── Entity Types ──────────────────────────────────────────────────────────

export type CompanyFormData = Omit<
	z.infer<typeof Dtos.Company.Create.FromSubmission.Request.schema>,
	"address"
> & {
	address: Address | null | undefined;
};

// ─── Individual Types ────────────────────────────────────────────────────

export type OwnerFormData = Omit<
	z.infer<typeof Dtos.Individual.Create.Request.ownerSchema>,
	"address"
> & {
	address?: Address | undefined | null;
};

export type SpouseFormData = Omit<
	z.infer<typeof Dtos.Individual.Create.Request.individualSchema>,
	"address"
> & {
	address?: Address | undefined | null;
};

export type IndividualFormData = OwnerFormData | SpouseFormData;
export type IndividualGroup = IndividualFormData[];

export const createDefaultOwner = (): Partial<OwnerFormData> & { type: "owner" } => ({
	type: "owner",
});

// ─── Typed Field Path ───────────────────────────────────────────────────────

export const fp = (path: Path<AccountFormValues>): string => path;

export const toAccountCreateRequest = (
	data: AccountCreateFormData | CombinedFormData,
): Dtos.SuretyAccount.Create.Request => {
	if (!("quote" in data)) {
		const { requiresPaymentAndPerformanceBonds, ...rest } = data;
		return {
			...rest,
			aiEnabled: true,
			account: {
				...rest.account,
				requiresPaymentAndPerformanceBonds: Option.getOrElse(
					requiresPaymentAndPerformanceBonds,
					true,
				),
			},
		};
	}

	const {
		bondType,
		contractBondType: _contractBondType,
		commercialBond: _commercialBond,
		quote,
		requiresPaymentAndPerformanceBonds,
		...rest
	} = data;
	return {
		...rest,
		aiEnabled: true,
		account: {
			...rest.account,
			requiresPaymentAndPerformanceBonds: Option.getOrElse(
				requiresPaymentAndPerformanceBonds,
				true,
			),
		},
		...(isCommercialSuretyBondType(bondType) ? {} : { quote }),
	};
};

// ─── Default Values ─────────────────────────────────────────────────────────

export const getDefaultAccountFormData = (): AccountCreateFormData => ({
	account: {
		...getSchemaDefaults(AccountRequest.aiLargeAccountSubmitSchema),
		details: {
			companies: [],
			individuals: [],
		},
	},
});

export const getDefaultCombinedFormData = (): CombinedFormData => ({
	account: {
		...getSchemaDefaults(AccountRequest.aiLargeAccountSubmitSchema),
		details: {
			companies: [],
			individuals: [],
		},
	},
	quote: {
		// Merge the quote union so an optional bid-to-final project does not wipe the bid/final project object.
		...getSchemaDefaults(QuoteSchemas.Request.quoteSchema),
		suretyType: "contract",
	},
	commercialBond: getSchemaDefaults(commercialBondFormSchema),
});
